import { Eye, EyeOff } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { TOOLBAR_CONTROL_SHELL, TOOLBAR_CONTROL_ICON, TOOLBAR_CONTROL_LABEL } from './toolbarControlClasses';

/**
 * SAB-950: the hide-drafted control, shared by both draft rooms.
 *
 * ── WHY THIS IS A SWITCH AND NOT A PILL ─────────────────────────────────────
 *
 * SAB-932 shipped it as a small pill sitting in the position-filter row, and it worked but
 * did not READ as a control: it looked like an eleventh position filter, and nothing about
 * it said "you can act on this". Adam reported both from the live room.
 *
 * This is the AutoDraftControl treatment (SAB-881), reused rather than re-invented, because
 * that ticket solved exactly this problem in exactly this room. The semantic half is the
 * part that matters most and cannot be undone by restyling: a `Switch` is role="switch"
 * with aria-checked, so it cannot be mistaken for a view tab or a filter pill by anything
 * that reads the page. The position filters next to it are ordinary buttons; this is not.
 *
 * ⚠️ SHARED ON PURPOSE. In V2 this sits adjacent to AutoDraftControl in the centre toolbar.
 * Two toggles side by side that are visibly different KINDS of thing is the defect this
 * fixes, so they must stay siblings — change one treatment and you should be changing both,
 * which is much likelier to happen if the code says so.
 *
 * ⚠️ OFF MUST NOT LOOK DISABLED. Muted foreground plus a faint border is the disabled
 * vocabulary; off here is idle but live. It keeps a full-contrast label, a solid border and
 * an enabled switch. SAB-881 called this out specifically: a control that looks disabled is
 * a control nobody presses — and the old off state used `text-muted-foreground` with
 * `border-transparent`, which is precisely that vocabulary.
 *
 * ── PRESENTATION ONLY ───────────────────────────────────────────────────────
 *
 * The behaviour is `filterDraftPool`'s and is unchanged: hidden by default while browsing,
 * ALWAYS shown while searching (the SAB-843 defect — a hunted player must never silently
 * vanish), a position filter counts as browsing, and the preference persists per user per
 * league. Nothing here decides any of that; this only says what the current state is.
 */

export interface HideDraftedControlProps {
  /** Current state. The label states THIS, not the action — see below. */
  hidden: boolean;
  onChange: (next: boolean) => void;
  /** Rows being withheld right now. 0 while searching. Omit to hide the badge. */
  hiddenCount?: number;
  /**
   * The rooms had different ids before this component existed and their suites assert on
   * them. Kept per-room rather than standardised, so this change stays presentation-only
   * and does not quietly invalidate assertions in two files.
   */
  testId?: string;
  switchTestId?: string;
  className?: string;
}

export default function HideDraftedControl({
  hidden, onChange, hiddenCount, testId = 'pool-hide-drafted',
  switchTestId = 'pool-hide-drafted-switch', className,
}: HideDraftedControlProps) {
  const inputId = `${switchTestId}-input`;

  return (
    <div
      data-testid={testId}
      // Preserved verbatim from the pill so the existing SAB-932/SAB-843 assertions keep
      // their meaning: the state is readable off the container, not inferred from styling.
      data-hide-drafted={String(hidden)}
      title={hidden
        ? 'Drafted players are hidden while browsing. They always appear when you search.'
        : 'Drafted players are shown, dimmed.'}
      className={cn(
        TOOLBAR_CONTROL_SHELL,
        hidden && 'border-primary/55 bg-primary/[0.10]',
        // OFF: solid border, ordinary surface, full-contrast text. Deliberately NOT
        // `text-muted-foreground`, `border-transparent` or `opacity-*`.
        !hidden && 'border-border bg-muted/40',
        className,
      )}
    >
      {/* Third channel after colour and wording, so the states are not told apart by hue
          alone — the same reason AutoDraftControl swaps its icon. */}
      {hidden
        ? <EyeOff className={cn(TOOLBAR_CONTROL_ICON, 'text-primary')} />
        : <Eye className={cn(TOOLBAR_CONTROL_ICON, 'text-foreground')} />}

      <label
        htmlFor={inputId}
        className={cn(
          TOOLBAR_CONTROL_LABEL,
          hidden ? 'text-primary' : 'text-foreground',
        )}
      >
        {/* Labels the CURRENT state, the way a checkbox does. Labelling it with the action
            it will perform reads backwards on a control that is also a status light. */}
        {hidden ? 'HIDE DRAFTED' : 'SHOW DRAFTED'}
        {/*
          The count is the honesty of the control: it says how many rows are being withheld
          right now, so "where did he go?" -- the question SAB-843 was filed about -- has a
          visible answer even before the manager thinks to search.
        */}
        {hidden && (hiddenCount ?? 0) > 0 && (
          <span data-testid="pool-hidden-count" className="ml-1 tabular-nums opacity-70">
            {hiddenCount}
          </span>
        )}
      </label>

      <Switch
        id={inputId}
        data-testid={switchTestId}
        aria-label="Hide drafted players while browsing"
        checked={hidden}
        onCheckedChange={onChange}
      />
    </div>
  );
}
